// 예: src/services/backgroundSync.ts 또는 App.vue

import { watch } from 'vue'
import { Network } from '@capacitor/network'
import { isApp } from '@/index'
import { SyncManager } from '@/app/sync/syncManager'
import { useAuthStore } from '@/app/data/auth'

// 타이머 관리를 위한 변수
let syncTimer: number | null = null
let signatureCleanupTimer: number | null = null
// 이벤트 트리거(로그인 복원/네트워크 복귀) 중복 등록 방지
let eventTriggersRegistered = false

/**
 * 주기적으로 모든 미동기화 데이터를 찾아 큐에 추가하는 함수.
 * 기존의 updateList 역할을 대체합니다.
 * SyncManager 내부에서 락을 관리하므로 별도의 isSyncing 상태가 필요 없음.
 */
async function triggerGlobalSync(): Promise<void> {
  if (!isApp()) return

  // 로그인 상태 확인 (로그아웃 후 API 호출 방지)
  const authStore = useAuthStore()
  if (!authStore.isLoggedIn) return

  try {
    await SyncManager.syncAllPendingData()
  } catch (error) {
    console.error('주기적 전체 동기화 중 오류 발생:', error)
  }
}

// 직전 네트워크 연결 상태 (전환 판정용)
let lastNetworkConnected: boolean | null = null
// 네트워크 복귀 트리거 최소 간격 — 안드로이드는 Wi-Fi 신호 재평가/망 전환 시
// 같은 connected=true 이벤트를 수 초 간격으로 반복 발화하므로 전체 동기화 폭주를 막는다
// (놓친 트리거는 2분 주기 안전망 타이머가 처리)
const NETWORK_TRIGGER_COOLDOWN_MS = 30 * 1000
let lastNetworkTriggeredAt = 0

// 로그인 복원/네트워크 복귀 시 타이머를 기다리지 않고 즉시 동기화
function registerEventTriggers(): void {
  if (eventTriggersRegistered) return
  eventTriggersRegistered = true

  const authStore = useAuthStore()
  watch(
    () => authStore.isLoggedIn,
    (loggedIn, wasLoggedIn) => {
      if (loggedIn && !wasLoggedIn) {
        console.info('[PhotoGuard] global sync triggered: login restored')
        triggerGlobalSync()
      }
    },
  )

  Network.addListener('networkStatusChange', status => {
    const wasConnected = lastNetworkConnected
    lastNetworkConnected = status.connected
    if (!status.connected) return
    // 실제 재연결(끊김→연결)만 트리거 — 같은 상태의 반복 이벤트는 무시
    if (wasConnected === true) return
    const now = Date.now()
    if (now - lastNetworkTriggeredAt < NETWORK_TRIGGER_COOLDOWN_MS) return
    lastNetworkTriggeredAt = now
    console.info('[PhotoGuard] global sync triggered: network reconnected')
    triggerGlobalSync()
  })
}

/**
 * 동기화 타이머를 시작합니다.
 * 타이머(2분)는 안전망이고, 실제 즉시성은 이벤트 트리거(로그인 복원/네트워크 복귀)가 담당합니다.
 */
export function startSyncTimer(): void {
  if (!isApp()) return
  if (syncTimer) clearInterval(syncTimer)
  if (signatureCleanupTimer) clearInterval(signatureCleanupTimer)

  registerEventTriggers()
  triggerGlobalSync()

  // 2분마다 반복 실행 (메모리 압박 완화)
  syncTimer = setInterval(() => {
    triggerGlobalSync()
  }, 120000)
}

/**
 * 수동으로 동기화를 트리거합니다. (initData 완료 후 호출용)
 */
export function triggerManualSync(): Promise<void> {
  return triggerGlobalSync()
}

/**
 * 동기화 타이머를 중지합니다.
 */
export function stopSyncTimer(): void {
  if (syncTimer) {
    clearInterval(syncTimer)
    syncTimer = null
  }
  if (signatureCleanupTimer) {
    clearInterval(signatureCleanupTimer)
    signatureCleanupTimer = null
  }
}
